"use client";
import React, { ReactNode, Suspense, createContext, useContext, useEffect, useState } from "react";
import Script from "next/script";
import { DEMO_ACCOUNT_ID, DEMO_USER_EMAIL } from "@/constants";
import { toast } from "react-hot-toast";
import { classNames, presetColorThemes } from "@/lib/utils";
import { AccountByIdData, useUpdateAccountMutation } from "@/utils/react-query-hooks";
import { useConfettiContext } from "@/contexts/ConfettiContextProvider";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { match } from "path-to-regexp";
import { usePathname } from "next/navigation";
import { UserContext } from "@/app/AppProviders";
import { AccountsContext, UserProfileContext } from "@/app/app/KeesyClientLayout";
import ImageWithFallback from "@/components/presentational/tailwind/ImageWithFallback";
import { Table } from "@/types";
import { LoadingSpinner } from "src/components/presentational/tailwind/LoadingSpinner";
import Card from "src/components/presentational/tailwind/Card";

// Accounts context
export const SelectedAccountContext = createContext({
  account: null,
  setAccountContext: (account: Table<'accounts'> | null) => { }
});

export function AccountClientLayout({ children, initialAccountByIdData, accountId }: { children: ReactNode; initialAccountByIdData?: AccountByIdData, accountId: string }) {

  console.log("🚀 ~ file: AccountClientLayout.tsx ~ accountId", accountId)
  const userProfile = useContext(UserProfileContext)
  console.log("🚀 ~ file: AccountClientLayout.tsx ~ userProfile", userProfile)
  const accounts = useContext(AccountsContext)
  const user = useContext(UserContext)
  const [account, setAccount] = useState<Table<'accounts'> | null>(null);
  const setAccountContext = (updatedAccount: Table<'accounts'> | null) => {
    setAccount(updatedAccount);
  };
  useEffect(() => {
    // Initial fetch to set the account state
    let accountData = accounts.find(acc => acc.id === accountId);
    if (accountData && typeof accountData.targeting_options === 'string')
      accountData.targeting_options = JSON.parse(accountData.targeting_options);
    setAccount(accountData);
  }, [accounts, accountId]);

  const [warmupScore, setWarmupScore] = useState<number>(0);
  useEffect(() => {
    if (!account?.created_at) return;
    if (!account?.active) return setWarmupScore(-1);
    const minutesSinceSignUp = (Date.now() - new Date(account.created_at).getTime()) / 60000; // difference in minutes
    const warmupScores = [
      // Time since sign up in minutes
      { limit: 10, score: 10 }, // 10 minutes
      { limit: 30, score: 15 }, // 30 minutes
      { limit: 60, score: 20 }, // 1 hour
      { limit: 180, score: 30 }, // 3 hours
      { limit: 480, score: 35 }, // 8 hours
      { limit: 720, score: 40 }, // 12 hours
      { limit: 1440, score: 50 }, // 1 day
      { limit: 2160, score: 55 }, // 1.5 days
      { limit: 2880, score: 60 }, // 2 days
      { limit: 4320, score: 70 }, // 3 days
      { limit: 5760, score: 75 }, // 4 days
      { limit: 7200, score: 77 }, // 5 days
      { limit: 8640, score: 79 }, // 6 days
      { limit: 10080, score: 80 }, // 1 week
      { limit: 13440, score: 82 }, // 1.5 weeks
      { limit: 20160, score: 85 }, // 2 weeks
      { limit: 26880, score: 87 }, // 2.5 weeks
      { limit: 30240, score: 90 }, // 3 weeks
      { limit: 33600, score: 92 }, // 3.5 weeks
      { limit: 36960, score: 95 }, // 4 weeks
      { limit: 40320, score: 97 }, // 4.5 weeks
      { limit: 43680, score: 98 }, // 5 weeks
    ];
    setWarmupScore(warmupScores.find(score => minutesSinceSignUp <= score.limit)?.score || 99);
  }, [account]);

  const { mutate: mutateAccount, isLoading: isLoadingAccountUpdate } = useUpdateAccountMutation({
    accountId: account?.id, hideToast: true,
    onSuccess: (data) => {
      // Assuming the mutation response returns the updated account data
      setAccount(data);
    },
  });
  const { setIsUpgradeModalOpened } = useModalContext();
  const { setShowConfetti } = useConfettiContext();

  const pathname = usePathname() || '';
  const matchAnalyticsPath = match("/app/account/:accountId/analytics/(.*)?");
  const isAnalyticsPath = matchAnalyticsPath(pathname);
  const isDemoAccount = account && account.id === DEMO_ACCOUNT_ID;

  function toggleActiveAccount(): void {
    if (account?.account_type === "trial") {
      // Show upgrade account modal
      // setModalVisible(true);
      console.log("Upgrade account modal...................");
      setIsUpgradeModalOpened(true);
    } else {

      mutateAccount({ active: !account?.active });
      toast.loading(account.active ? "Pausing growth..." : "Starting growth...", { id: "toggleActiveAccount" });
      toast.dismiss();
      toast.success(account.active ? "Growth paused successfully!" : "Growth started successfully!", { id: "toggleActiveAccount" });
      // Show confetti if account is now active
      if (!account.active) {
        // Stop confetti after 3 seconds
        setShowConfetti(true);
        setTimeout(() => {
          setShowConfetti(false);
        }, 3000);
      }
    }
  }

  const getMainButtonThemeClasses = (themeName: string) => (presetColorThemes.find(theme => theme.name === themeName) || presetColorThemes[0]).mainButton;
  const getShadowGradientThemeClass = (themeName: string) => (presetColorThemes.find(theme => theme.name === themeName) || presetColorThemes[0]).pageShadowGradient;

  const getWarmupScoreColor = (warmupScore: number): string => {
    if (warmupScore > 85)
      return "green";
    else if (warmupScore > 70)
      return "fuchsia";
    else if (warmupScore > 50)
      return "blue";
    else if (warmupScore > 30)
      return "orange"
    else if (warmupScore == -1)
      return "rose"
    else
      return "amber";
  }
  const HtmlContent = ({ html }) => <div dangerouslySetInnerHTML={{ __html: html }} />;
  const WarmupScoreComponent = (
    <div className="flex items-center space-x-1">
      <p className={classNames("text-[17px] font-bold font-dashboard",
        `text-${getWarmupScoreColor(warmupScore)}-600`
      )}>
        {warmupScore > 85 ? "Excellent" :
          warmupScore > 70 ? "Great" :
            warmupScore > 50 ? "Good" :
              warmupScore > 30 ? "Optimizing" :
                warmupScore > 15 ? "Training" :
                  warmupScore == -1 ? "Paused"
                    : "Started..."}
      </p>
      {/* <QuestionMarkIcon className="w-4 h-4 !ml-2 place-self-center ignore fill-gray-300" /> */}
      <svg className='w-4 h-4 !ml-2 place-self-center ignore fill-gray-300' stroke="none" xmlns="http://www.w3.org/2000/svg" strokeWidth="1.7px" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" viewBox="0 0 24 24" style={{ boxSizing: "border-box", position: "relative", flex: "0 0 auto" }}>
        <path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12.0023 18.458H12.0115L12.0023 18.4488C12.758 18.4488 13.385 17.8236 13.3875 17.0687C13.3875 17.0702 13.3875 17.0718 13.3875 17.0733V17.0641C13.3875 17.0656 13.3875 17.0672 13.3875 17.0687C13.385 16.3046 12.758 15.6886 12.0023 15.6886C11.2357 15.6886 10.617 16.3071 10.617 17.0733C10.617 17.8303 11.2357 18.4488 12.0023 18.458ZM13.4891 12.8361V12.8453L13.485 12.8378C13.4863 12.8372 13.4877 12.8367 13.4891 12.8361ZM12.935 13.6762C12.9258 13.3084 13.1456 12.9773 13.485 12.8378L13.443 12.7623C14.7913 12.1715 15.6686 10.8514 15.6686 9.38357C15.6686 7.35266 14.0063 5.69101 11.9746 5.70024C9.9428 5.70024 8.28045 7.36189 8.28969 9.40203L8.29892 9.41126C8.29892 9.91899 8.70527 10.3344 9.22245 10.3344C9.73039 10.3344 10.146 9.91899 10.146 9.41126C10.146 8.67275 10.58 8.00809 11.2542 7.72192C12.187 7.31574 13.2767 7.74038 13.6923 8.68198C14.0987 9.61435 13.6738 10.7037 12.7411 11.1191V11.1283C11.7252 11.5622 11.0695 12.5592 11.0787 13.6669L11.088 13.6762C11.088 14.1839 11.4943 14.5993 12.0115 14.5993C12.5194 14.5993 12.935 14.1839 12.935 13.6762Z" />
      </svg>
    </div>
  )

  if (!account) return (
    <Card className="flex items-center justify-center w-full h-[280px] mx-auto mt-2">
      <div className="w-full max-w-xs overflow-hidden animate-pulse">
        <LoadingSpinner className="m-auto text-pink-500 w-7 h-7" />
      </div>
    </Card>
  )

  return (
    <>
      <div className="inline-flex flex-col items-start justify-between w-full gap-4 lg:flex-row">

        <div className="inline-flex items-center justify-start gap-4">
          <div className="w-12 h-12 p-0.5 relative rounded-full border-1 border-pink-500 justify-center items-center flex">
            <ImageWithFallback
              unoptimized={true}
              loading='eager'
              width={60}
              height={60}
              className="relative rounded-full w-11 h-11"
              src={account.avatar_url || "/img/default_avatar.jpeg"}
              alt={`${account.username}'s profile image`}
              placeholder="blur"
              blurDataURL="/img/default_avatar.jpeg"
              fallback={"/img/default_avatar.jpeg"}
            />
            <div className="absolute bottom-[-3px] right-[-3px] w-4 h-4 border-[2px] border-white rounded-full"
              style={account.active ? { background: 'linear-gradient(153.43deg, #179942 -65.48%, #BEF264 89.29%)' } : { background: 'linear-gradient(153.43deg, #F87171 -65.48%, #FBBF24 89.29%)' }}
            />
          </div>
          <div className="inline-flex flex-col items-start justify-start gap-0.5 grow shrink basis-0">
            <div className="inline-flex items-center self-stretch justify-start gap-2">
              <div className="text-xl font-medium leading-normal tracking-tight text-left text-zinc-900">Welcome, {account?.name} 👋</div>
            </div>
            <div className="self-stretch text-sm leading-tight text-neutral-400">Sit back & watch the growth skyrocket!</div>
          </div>
        </div>

        <div className="flex items-center justify-start w-full gap-4 lg:w-auto">
          {(user.email == DEMO_USER_EMAIL || ['trial', 'basic', 'pro'].includes(account.account_type)) && (
          <button className="w-1/2 lg:w-auto group flex items-center justify-center gap-2 px-4 py-[11px] bg-white border border-white border-opacity-25 shadow hover:opacity-60 transition-opacity duration-200 ease-in-out"
              onClick={() => {
                if (user.email === DEMO_USER_EMAIL)
                  // Go to /start
                  window.location.href = '/start';
                else
                  setIsUpgradeModalOpened(true)
              }}
            style={{
              borderRadius: '9px',
              border: '1px solid var(--Gradients-White-Stroke, #FFF)',
              background: 'linear-gradient(180deg, rgba(223, 225, 231, 0.00) 0%, rgba(223, 225, 231, 0.05) 100%), var(--Neutral-0, #FFF)',
              boxShadow: '0px 0px 0px 1px rgba(225, 226, 228, 0.56), 0px 1px 2px 0px rgba(119, 124, 133, 0.12)',
            }}
          >
            <svg className="transition-transform group-hover:-rotate-12 w-[1.1rem] h-[1.1rem]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm font-medium leading-tight text-center text-gray-900">Upgrade Plan</span>
          </button>
          )}
          <button className="w-1/2 lg:w-auto flex items-center group justify-center gap-2 px-[18px] py-[11px] hover:opacity-90 transition-opacity duration-200 ease-in-out"
            onClick={toggleActiveAccount}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '9px',
              background: 'var(--Gradients-Primary-Primary-Button, linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.00) 100%), linear-gradient(135deg, #7549F4 2.88%, #FCA6E9 100%))',
              boxShadow: '0px 1px 2px rgba(47, 10, 154, 0.4), inset 0 0 0 1px rgba(255,255,255,.4)',
              border: '1px solid rgba(110, 63, 243, 0.4)',
              filter: "saturate(1.15)"
            }}
          >
            {account.active ? (
              <svg className="transition-transform group-hover:-rotate-12 w-[1.1rem] h-[1.1rem]" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2004_808)"><path d="M6.3335 10V6.00004M9.66683 10V6.00004M14.6668 8.00004C14.6668 11.6819 11.6821 14.6667 8.00016 14.6667C4.31826 14.6667 1.3335 11.6819 1.3335 8.00004C1.3335 4.31814 4.31826 1.33337 8.00016 1.33337C11.6821 1.33337 14.6668 4.31814 14.6668 8.00004Z" stroke="#F3F4F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></g><defs><clipPath id="clip0_2004_808"><rect width="16" height="16" fill="white" /></clipPath></defs></svg>
            ) : (
              <svg className="w-[1.1rem] h-[1.1rem] text-white transition-transform group-hover:-rotate-12" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#F3F4F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9.5 8.96533C9.5 8.48805 9.5 8.24941 9.59974 8.11618C9.68666 8.00007 9.81971 7.92744 9.96438 7.9171C10.1304 7.90525 10.3311 8.03429 10.7326 8.29239L15.4532 11.3271C15.8016 11.551 15.9758 11.663 16.0359 11.8054C16.0885 11.9298 16.0885 12.0702 16.0359 12.1946C15.9758 12.337 15.8016 12.449 15.4532 12.6729L10.7326 15.7076C10.3311 15.9657 10.1304 16.0948 9.96438 16.0829C9.81971 16.0726 9.68666 15.9999 9.59974 15.8838C9.5 15.7506 9.5 15.512 9.5 15.0347V8.96533Z" stroke="#F3F4F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            <span className="text-sm font-medium leading-tight text-white text-cente text-shadow">
              {account.active ? "Pause Growth" : "Start Growth"}
            </span>
          </button>

        </div>
      </div>


      {/* Account Page Body Section */}
      <section className="pt-6 animate-fade-in">
        <Suspense>
          <SelectedAccountContext.Provider value={{ account, setAccountContext }}>

            {children}

            {/* {userProfile && (<p>{JSON.stringify(userProfile)}</p>)} */}
            {(accounts && accounts.length) && (
              <Script id="intercom-settings">
                {`
                  try {
                    window.intercomSettings = ${user.email === DEMO_USER_EMAIL
                    ? `{
                          api_base: "https://api-iam.intercom.io",
                          app_id: "g6b4q3en"
                        }`
                    : `{
                          api_base: "https://api-iam.intercom.io",
                          app_id: "g6b4q3en",
                          user_id: "${user.id}",
                          email: "${user.email}",
                          created_at: ${Math.floor(new Date(user.created_at).getTime() / 1000)},
                          ${userProfile.full_name ? `name: "${userProfile.full_name}",` : ''}
                          avatar: {
                            type: "avatar", 
                            image_url: "${userProfile.avatar_url}",
                          },
                          "Is Customer": ${accounts.some(acc => ["basic", "pro", "turbo"].includes(acc.account_type)) || accounts.some(acc => acc.customer_id !== null)},
                          has_accounts: ${accounts.length > 0},
                          companies: ${accounts.length > 0 ?
                      `[${accounts.map(acc => `{
                              company_id: "${acc.id}",
                              created_at: "${Math.floor(new Date(acc.created_at).getTime() / 1000)}",
                              name: "${acc.username}",
                              plan: "${acc.account_type}",
                              size: "${acc.followers}",
                              followers: "${acc.followers}",
                              active: "${acc.active == true ? true : false}",
                              website: "https://www.instagram.com/${acc.username}",
                            }`)}]`
                      : null}
                        }`
                  };
                  } catch (error) {
                    console.log(error);
                  }
                  `}
              </Script>
            )}
          </SelectedAccountContext.Provider>
        </Suspense>
      </section>
    </>
  );
}
